import React, { createContext, useMemo } from "react";
import { useAuth } from "utils/hooks";
import API_ENDPOINTS from "../../api/apiEndpoints";

import * as gnssApi from "../../api/gnssApiRequests"; // Import everything as `postsApi`

/**
 * @typedef {Object} GnssApiContextType
 * @property {Function} getAllStationsDetails
 * @property {Function} addStationDetails
 * @property {Function} updateStationDetails
 * @property {Function} deleteStation
 * @property {Function} getLastReportByStation
 * @property {Function} getReportByStationAndDate
 * @property {Function} getReportsDatesById
 * @property {Function} deleteReport
 */

/**
 * React Context for GNSS API operations.
 * @type {React.Context<GnssApiContextType>}
 */
export const GnssApiContext = createContext();

/**
 * Provider component that exposes GNSS API methods to children components.
 * Handles token refresh logic on 401 responses.
 * @exports GnssApiProvider
 * @memberof module:providers
 * @function
 * @name GnssApiProvider
 * @param {Object} props
 * @param {React.ReactNode} props.children - Child components that need access to the GNSS API.
 * @returns {JSX.Element}
 */
export const GnssApiProvider = ({ children }) => {
  const {
    selected_tenant_id: tenantId,
    tenant_access_token: access_token,
    onLogout,
    onRefreshToken,
  } = useAuth();
  const baseUrl = API_ENDPOINTS.gnssApi.baseUrl;

  /**
   * Handles common response logic, including token refresh if unauthorized.
   *
   * @param {Response} response - The fetch response object.
   * @param {Function|null} retryFunction - Function to retry request with a new token.
   * @returns {Promise<Object>} JSON response or error data.
   */
  const handleResponse = async (response, retryFunction) => {
    if (!response.ok && retryFunction !== null) {
      // Check if the error is due to an expired token
      if (response.code === 401) {
        // Try refreshing the token

        const refreshResponse = await onRefreshToken();
        if (refreshResponse.access_token) {
          // Retry the original request with the new token
          const retryResp = await retryFunction(refreshResponse.access_token);
          const retryData = await retryResp.json();

          return retryData;
        } else {
          return { status: response.status, message: "You are unauthorized" };
        }
      }

      if (response.code === 403) {
        // Try refreshing the token

        return { status: response.status, message: "You don't have rights" };
      }

      const errorData = await response.json();
      return errorData;
    }
    return response.json();
  };

  const gnssApiClient = useMemo(
    () => ({
      /**
       * Fetch all GNSS stations.
       */
      getAllStationsDetails: async () => {
        const response = await gnssApi.getAllStationsDetails(
          access_token,
          tenantId
        );
        return handleResponse(response, (newToken) =>
          gnssApi.getAllStationsDetails(newToken, tenantId)
        );
      },
      /**
       * Add a new station to a group.
       *
       * @param {string} groupName
       * @param {Object} stationDetailsPayload
       */
      addStationDetails: async (stationDetailsPayload) => {
        const response = await gnssApi.addStationDetails(
          stationDetailsPayload.groupNames[0],
          stationDetailsPayload.identifier,
          stationDetailsPayload,
          access_token,
          tenantId
        );
        return handleResponse(response, (newToken) =>
          gnssApi.addStationDetails(
            stationDetailsPayload.groupNames[0],
            stationDetailsPayload.identifier,
            stationDetailsPayload,
            newToken,
            tenantId
          )
        );
      },
      /**
       * Update station information.
       *
       * @param {string} groupName
       * @param {string} stationId
       * @param {Object} stationDetailsPayload
       */
      updateStationDetails: async (
        groupName,
        stationId,
        stationDetailsPayload
      ) => {
        const response = await gnssApi.updateStationDetails(
          groupName,
          stationId,
          stationDetailsPayload,
          access_token,
          tenantId
        );
        return handleResponse(response, (newToken) =>
          gnssApi.updateStationDetails(
            groupName,
            stationId,
            stationDetailsPayload,
            newToken,
            tenantId
          )
        );
      },
      /**
       * Delete a station by ID.
       *
       * @param {string} stationId
       */
      deleteStation: async (groupName, stationId) => {
        const response = await gnssApi.deleteStation(
          groupName,
          stationId,
          access_token,
          tenantId
        );
        return handleResponse(response, (newToken) =>
          gnssApi.deleteStation(groupName, stationId, newToken, tenantId)
        );
      },

      /**
       * Get the latest report by station.
       *
       * @param {string} stationId
       */
      getLastReportByStation: async (groupName, stationId) => {
        const response = await gnssApi.getLastReportByStation(
          groupName,
          stationId,
          access_token,
          tenantId
        );
        return handleResponse(response, (newToken) =>
          gnssApi.getLastReportByStation(
            groupName,
            stationId,
            newToken,
            tenantId
          )
        );
      },
      /**
       * Get report by station and date.
       *
       * @param {string} stationId
       * @param {string} date - ISO date string (e.g. "2025-07-07").
       */
      getReportByStationAndDate: async (groupName, stationId, date) => {
        const response = await gnssApi.getReportByStationAndDate(
          groupName,
          stationId,
          date,
          access_token,
          tenantId
        );
        return handleResponse(response, (newToken) =>
          gnssApi.getReportByStationAndDate(
            groupName,
            stationId,
            date,
            newToken,
            tenantId
          )
        );
      },
      /**
       * Get available report dates for a station.
       *
       * @param {string} stationId
       */
      getReportsDatesById: async (groupName, stationId) => {
        const response = await gnssApi.getReportsDatesById(
          groupName,
          stationId,
          access_token,
          tenantId
        );
        return handleResponse(response, (newToken) =>
          gnssApi.getReportsDatesById(groupName, stationId, newToken, tenantId)
        );
      },
      /**
       * Delete a report for a station by date.
       *
       * @param {string} stationId
       * @param {string} date
       */
      deleteReport: async (groupName, stationId, date) => {
        const response = await gnssApi.deleteReport(
          groupName,
          stationId,
          date,
          access_token,
          tenantId
        );
        return handleResponse(response, (newToken) =>
          gnssApi.deleteReport(stationId, date, newToken, tenantId)
        );
      },

      getSpectrogramImageByStationId: async (
        groupName,
        stationId,
        targetWidth,
        targetHeight
      ) => {
        const response = await gnssApi.getSpectrogramImage(
          groupName,
          stationId,
          targetWidth,
          targetHeight,
          access_token,
          tenantId
        );
        return handleResponse(response, (newToken) =>
          gnssApi.getSpectrogramImage(
            groupName,
            stationId,
            targetWidth,
            targetHeight,
            newToken,
            tenantId
          )
        );
      },
      getAlertsByStationId: async (groupName, stationId, limit) => {
        const response = await gnssApi.getAlertsByStationId(
          groupName,
          stationId,
          limit,
          access_token,
          tenantId
        );
        return handleResponse(response, (newToken) =>
          gnssApi.getAlertsByStationId(
            groupName,
            stationId,
            limit,
            newToken,
            tenantId
          )
        );
      },
      getAlertsByStationIdFiltered: async (groupName, stationId, filters) => {
        const response = await gnssApi.getAlertsByStationIdFiltered(
          groupName,
          stationId,
          filters,
          access_token,
          tenantId
        );
        return handleResponse(response, (newToken) =>
          gnssApi.getAlertsByStationIdFiltered(
            groupName,
            stationId,
            filters,
            newToken,
            tenantId
          )
        );
      },
      getAlertsIdsByStationId: async (groupName, stationId, date) => {
        const response = await gnssApi.getAlertsIdsByStationId(
          groupName,
          stationId,
          date,
          access_token,
          tenantId
        );
        return handleResponse(response, (newToken) =>
          gnssApi.getAlertsIdsByStationId(
            groupName,
            stationId,
            date,
            newToken,
            tenantId
          )
        );
      },
       getAlertDetailsByStationId: async (groupName, stationId, alertId) => {
        const response = await gnssApi.getAlertByStationId(
          groupName,
          stationId,
          alertId,
          access_token,
          tenantId
        );
        return handleResponse(response, (newToken) =>
          gnssApi.getAlertByStationId(
            groupName,
            stationId,
            alertId,
            newToken,
            tenantId
          )
        );
      },
      getAlertAnalyticsByStationId: async (groupName, stationId, alertId) => {
        const response = await gnssApi.getAlertAnalyticsByStationIdAndAlertId(
          groupName,
          stationId,
          alertId,
          access_token,
          tenantId
        );

        return handleResponse(response, (newToken) =>
          gnssApi.getAlertAnalyticsByStationIdAndAlertId(
            groupName,
            stationId,
            alertId,
            newToken,
            tenantId
          )
        );
      },
      
      getAllAnalyticsByStationId: async (groupName, stationId) => {
        const response = await gnssApi.getAllAnalyticsByStationId(
          groupName,
          stationId,
          access_token,
          tenantId
        );

        return handleResponse(response, (newToken) =>
          gnssApi.getAllAnalyticsByStationId(
            groupName,
            stationId,
            newToken,
            tenantId
          )
        );
      },
      getAnalyticsByStationIdAndDateInterval: async (groupName, stationId,startDate,stopDate) => {
        const response = await gnssApi.getAnalyticsByStationIdAndDateInterval(
          groupName,
          stationId,
          startDate,
          stopDate,
          access_token,
          tenantId
        );

        return handleResponse(response, (newToken) =>
          gnssApi.getAnalyticsByStationIdAndDateInterval(
            groupName,
            stationId,
            startDate,
            stopDate,
            newToken,
            tenantId
          )
        );
      },
    }),
    
    [baseUrl, access_token]
  );

  return (
    <GnssApiContext.Provider value={gnssApiClient}>
      {children}
    </GnssApiContext.Provider>
  );
};
